import {
  getHeaderImage,
  DEFAULT_LOGO,
  getSessionDateLabel,
  getSessionTimeLabel,
  eventTitle as getEventTitle,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { DateTimeLayout } from './widgets/date-time-layout';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.viceroy;

const VICEROY_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { size: 'original', height: 40, align: 'center', padding: '24px' },
  [BLOCK_ID.badge]: {
    fitContent: true,
    align: 'center',
    color: 'var(--secondary-bg)',
    fontFamily: 'var(--heading-font)',
    fontSize: '14px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    background: 'var(--secondary-accent)',
    borderRadius: '100px',
    padding: '8px 16px',
    margin: '0 56px 20px 56px',
  },
  [BLOCK_ID.title]: {
    color: '#F6F5EA',
    fontFamily: 'var(--heading-font)',
    fontSize: '64px',
    fontWeight: 700,
    lineHeight: '54px',
    padding: '0 56px 40px 56px',
  },
  [BLOCK_ID.mainImage]: { width: 600, height: 400, borderRadius: 'var(--image-radius)' },
  [BLOCK_ID.description]: {
    color: '#F6F5EA',
    fontFamily: 'var(--body-font)',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '29px',
    padding: '48px 56px 32px 56px',
  },
  [BLOCK_ID.cta]: {
    fullWidth: true,
    size: 'large',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--heading-font)',
    fontWeight: 700,
    padding: '20px 56px',
  },
  [BLOCK_ID.legalText]: {
    color: '#FFEAED',
    fontFamily: 'var(--body-font)',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '17px',
    opacity: 0.8,
    padding: '56px 56px 40px 56px',
  },
  // the date & time block keeps viceroy's original fixed-size stacked card
  [BLOCK_ID.dateTimeCard]: {
    layout: DateTimeLayout.Stacked,
    padding: '12px 56px',
    cardBackground: '#F6F5EA',
    cardBorderRadius: '4px',
    cardWidth: 321,
    cardHeight: 166,
    borderStyle: 'none',
    dateRowPadding: '32px',
    groupGap: '24px',
    timeHeading: DEFAULTS.startTimeLabel,
    labelStyle: { color: '#666666', opacity: 0.6, fontSize: '12px', fontWeight: 700, marginBottom: '4px' },
    valueStyle: { color: 'var(--container)', fontSize: '20px' },
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Viceroy = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const secondaryBg = resolveThemedColor(
    customizations?.secondaryBackgroundColor,
    themeColors?.secondaryBackgroundColor,
    DEFAULTS.secondaryBackgroundColor
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '4px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(
    sessionTimestamp,
    timezone,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
    { dayFirst: true, commaBeforeYear: true }
  );
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || DEFAULT_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Archivo Black", Arial Black, sans-serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    'Archivo, Arial, sans-serif'
  );
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription =
    description || customizations?.description || DEFAULTS.descriptionTemplate?.(getEventTitle(event, 'en'));
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      secondaryBg,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: VICEROY_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Viceroy"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--heading': heading,
            '--secondary-bg': secondaryBg,
            '--button-text': buttonText,
            '--body': body,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px', background: 'var(--container)' }}
              >
                <tbody>
                  <tr>
                    <td>
                      {blocks.map((block) => {
                        if (block.id === BLOCK_ID.description && !eventDescription) return null;

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={VICEROY_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Viceroy"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--heading': heading,
          '--secondary-bg': secondaryBg,
          '--button-text': buttonText,
          '--body': body,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td>
                    <LogoWidget
                      src={headerImg}
                      setImage={setHeaderImage}
                      size="original"
                      height={40}
                      align="center"
                      padding="24px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={DEFAULTS.liveBadgeText}
                      fitContent
                      align="center"
                      color="var(--secondary-bg)"
                      fontFamily="var(--heading-font)"
                      fontSize="14px"
                      fontWeight={700}
                      whiteSpace="nowrap"
                      background="var(--secondary-accent)"
                      borderRadius="100px"
                      padding="8px 16px"
                      margin="0 56px 20px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="#F6F5EA"
                      fontFamily="var(--heading-font)"
                      fontSize="64px"
                      fontWeight={700}
                      lineHeight="54px"
                      padding="0 56px 40px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={600}
                      height={400}
                      borderRadius={imageRadius}
                    />
                  </td>
                </tr>

                {eventDescription && (
                  <tr>
                    <td>
                      <ParagraphWidget
                        value={eventDescription}
                        setValue={setDescription}
                        color="#F6F5EA"
                        fontFamily="var(--body-font)"
                        fontSize="24px"
                        fontWeight={700}
                        lineHeight="29px"
                        padding="48px 56px 32px 56px"
                      />
                    </td>
                  </tr>
                )}

                {sessionTimestamp && (
                  <tr>
                    <td align="left" style={{ padding: '12px 56px' }}>
                      <table
                        width={321}
                        cellPadding={0}
                        cellSpacing={0}
                        border={0}
                        role="presentation"
                        style={{ background: '#F6F5EA', borderRadius: '4px' }}
                      >
                        <tbody>
                          <tr>
                            <td height={166} style={{ padding: '32px' }}>
                              <div
                                style={{
                                  color: '#666666',
                                  opacity: 0.6,
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  marginBottom: '4px',
                                }}
                              >
                                Date
                              </div>
                              <div
                                style={{
                                  color: 'var(--container)',
                                  fontSize: '20px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  marginBottom: '24px',
                                }}
                              >
                                {sessionDateLabel}
                              </div>
                              <div
                                style={{
                                  color: '#666666',
                                  opacity: 0.6,
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  marginBottom: '4px',
                                }}
                              >
                                {DEFAULTS.startTimeLabel}
                              </div>
                              <div
                                style={{
                                  color: 'var(--container)',
                                  fontSize: '20px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                }}
                              >
                                {sessionTimeLabel}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    <ButtonWidget
                      value={_cta}
                      setValue={setCta}
                      href={anchorHref}
                      fullWidth
                      size="large"
                      color="var(--button-text)"
                      buttonColor="var(--button-color)"
                      fontFamily="var(--heading-font)"
                      fontWeight={700}
                      borderRadius={buttonRadius}
                      padding="20px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '56px 56px 40px 56px' }}>
                    <p
                      style={{
                        margin: 0,
                        color: '#FFEAED',
                        opacity: 0.8,
                        fontSize: '11px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 400,
                        lineHeight: '17px',
                        wordWrap: 'break-word',
                      }}
                    >
                      {DEFAULTS.complianceText}
                    </p>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
